// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useState } from 'react';
import { Modal, Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Themed';
import appColors from '@/theme/colors';
import type { TourRoundSummary, SidebarSelection } from '../types';
import Sidebar from './Sidebar';

type Props = {
  tourName: string;
  playerCount: number;
  gameCount: number;
  rounds: TourRoundSummary[];
  selection: SidebarSelection;
  expandedRoundId: string | null;
  onSelect: (selection: SidebarSelection) => void;
  onExpandRound: (roundId: string | null) => void;
  onAddRound: () => void;
  onDeleteRound: (roundId: string) => void;
};

function selectionLabel(selection: SidebarSelection, rounds: TourRoundSummary[]): string {
  switch (selection.kind) {
    case 'tour-name':
      return 'Tour basics';
    case 'tour-players':
      return 'Players';
    case 'tour-games':
      return 'Games';
    case 'round-settings':
    case 'round-players':
    case 'round-games': {
      const index = rounds.findIndex((r) => r.id === selection.roundId);
      const round = index >= 0 ? rounds[index] : undefined;
      const roundLabel = round?.name || `Round ${index >= 0 ? index + 1 : ''}`.trim();
      const sub =
        selection.kind === 'round-settings'
          ? 'Settings'
          : selection.kind === 'round-players'
            ? 'Players'
            : 'Games';
      return `${roundLabel} · ${sub}`;
    }
    default:
      return 'Menu';
  }
}

export default function SidebarDropdown({ selection, rounds, onSelect, ...rest }: Props) {
  const [open, setOpen] = useState(false);

  const handleSelect = (sel: SidebarSelection) => {
    onSelect(sel);
    // Dismiss only when a leaf item is chosen (round headers keep the menu open
    // so the user can drill into sub-items). Round headers select 'round-settings'
    // alongside expand, but tapping any item here is a deliberate navigation.
    setOpen(false);
  };

  return (
    <>
      <Pressable style={styles.bar} onPress={() => setOpen(true)} accessibilityRole="button">
        <Text style={styles.barLabel} numberOfLines={1}>
          {selectionLabel(selection, rounds)}
        </Text>
        <Text style={styles.chevron}>▾</Text>
      </Pressable>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setOpen(false)}>
          <Pressable style={styles.sheet} onPress={(e) => e.stopPropagation()}>
            <Sidebar
              selection={selection}
              rounds={rounds}
              onSelect={handleSelect}
              {...rest}
            />
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: appColors.background.panel,
    borderBottomWidth: 1,
    borderBottomColor: appColors.border.default,
  },
  barLabel: {
    flex: 1,
    fontSize: 15,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  chevron: {
    fontSize: 14,
    color: appColors.text.muted,
    marginLeft: 8,
  },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  sheet: {
    maxHeight: '80%',
    backgroundColor: appColors.background.panel,
    borderBottomWidth: 1,
    borderBottomColor: appColors.border.default,
  },
});
